import { action, computed, makeObservable, observable, onBecomeObserved, runInAction } from "mobx";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { toastModule } from "@/modules/toast";
import { AppStore } from "@/store";
import { TextMessageSenderObservable } from "@/store/text-message-senders/TextMessageSenderObservable";
import { VerificationStatus } from "@/domains/integrations/verification-status";
import { api } from "@/modules/api";
import {
  ITextMessageSenderStore,
  TextMessageIntegrationFormattingMode,
  TextMessageIntegrationOrganizingMode,
  TextMessageIntegrationFormattingInstructions,
  TextMessageIntegrationOrganizingInstructions,
  TextMessageIntegrationOrganizingManualCollections,
  TextMessageIntegrationSettings,
} from "@/store/text-message-senders/types";
import { TrackedEvent } from "@/domains/metrics/tracked-event";
import { trackEvent } from "@/domains/metrics/segment";

export class AppStoreTextMessageSendersStore implements ITextMessageSenderStore {
  private data: TextMessageSenderObservable[] = [];
  private loading = false;
  private creating = false;
  private deleting = false;
  private verifying = false;
  private requestingNewVerificationCode = false;
  private loadingSettings = false;
  private savingSettings = false;

  private _formattingMode: TextMessageIntegrationFormattingMode = "smart";
  private _formattingInstructions: TextMessageIntegrationFormattingInstructions = null;
  private _organizingOption: TextMessageIntegrationOrganizingMode[] = [];
  private _organizingInstructions: TextMessageIntegrationOrganizingInstructions = null;
  private _organizingManualCollections: TextMessageIntegrationOrganizingManualCollections = [];

  private static CLIENT_ONLY_ID = "client-only-id";

  public static VERIFICATION_TIMEOUT = 60_000;

  constructor(private readonly store: AppStore) {
    makeObservable<
      AppStoreTextMessageSendersStore,
      | "data"
      | "loading"
      | "creating"
      | "deleting"
      | "verifying"
      | "requestingNewVerificationCode"
      | "loadingSettings"
      | "savingSettings"
      | "_formattingMode"
      | "_formattingInstructions"
      | "_organizingOption"
      | "_organizingInstructions"
      | "resetSettingFromResponse"
      | "_organizingManualCollections"
    >(this, {
      data: observable,
      items: computed,
      unVerifiedItem: computed,
      addUnverifiedItem: action,
      clearUnverifiedItem: action,
      verifiedItems: computed,

      // Load
      load: action,
      loading: observable,
      isLoading: computed,

      // Create
      create: action,
      creating: observable,
      isCreating: computed,

      // Verify
      verify: action,
      verifying: observable,
      isVerifying: computed,

      // Delete
      delete: action,
      deleting: observable,
      isDeleting: computed,

      // Request new verification code
      requestNewVerificationCode: action,
      requestingNewVerificationCode: observable,
      isRequestingNewVerificationCode: computed,

      // Settings
      _formattingMode: observable,
      _formattingInstructions: observable,
      _organizingOption: observable,
      _organizingInstructions: observable,
      _organizingManualCollections: observable,
      formattingMode: computed,
      formattingInstructions: computed,
      organizingOption: computed,
      organizingInstructions: computed,
      organizingManualCollections: computed,
      loadSettings: action,
      saveSettings: action,
      resetSettingFromResponse: action,
      setFormattingOption: action,
      setFormattingInstructions: action,
      setOrganizingOption: action,
      setOrganizingInstructions: action,
      setOrganizingManualCollections: action,
      loadingSettings: observable,
      savingSettings: observable,
      isLoadingSettings: computed,
      isSavingSettings: computed,
    });

    onBecomeObserved(this, "items", () => {
      this.load();
      this.loadSettings();
    });
  }

  get isLoading() {
    return this.loading;
  }

  get items() {
    return this.data;
  }

  get verifiedItems() {
    return this.data.filter(item => item.isVerified);
  }

  get isCreating() {
    return this.creating;
  }

  get isVerifying() {
    return this.verifying;
  }

  get isDeleting() {
    return this.deleting;
  }

  get unVerifiedItem() {
    return this.data.find(item => !item.isVerified);
  }

  get isRequestingNewVerificationCode() {
    return this.requestingNewVerificationCode;
  }

  get formattingMode() {
    return this._formattingMode;
  }

  get formattingInstructions() {
    return this._formattingInstructions;
  }

  get organizingOption() {
    return this._organizingOption;
  }

  get organizingInstructions() {
    return this._organizingInstructions;
  }

  get isLoadingSettings() {
    return this.loadingSettings;
  }

  get isSavingSettings() {
    return this.savingSettings;
  }

  get organizingManualCollections() {
    return this._organizingManualCollections;
  }

  load = async () => {
    runInAction(() => {
      this.loading = true;
    });

    try {
      const response = await api.get("/v2/integrations/text-messages/senders", {});

      runInAction(() => {
        this.data =
          response.data?.senders.map(sender => {
            const textMessageSender = new TextMessageSenderObservable(sender);
            if (!sender.is_verified) {
              textMessageSender.setVerificationStatus(VerificationStatus.AwaitsVerification);
            }
            return textMessageSender;
          }) ?? [];
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "Failed to load text message senders",
        info: { error: objectModule.safeErrorAsJson(error) },
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  create = async (phoneNumber: string) => {
    runInAction(() => {
      this.creating = true;
    });

    try {
      const response = await api.post("/v2/integrations/text-messages/senders", {
        body: {
          phone_number: phoneNumber,
        },
      });

      const data = response.data;
      if (!data || response.error) {
        const error = response.error as { detail: string };
        throw new Error(error.detail ?? "Failed to add text message sender");
      }

      runInAction(() => {
        const unVerifiedItem = this.unVerifiedItem;
        if (unVerifiedItem) {
          unVerifiedItem.reset(data);
          unVerifiedItem.setVerificationStatus(VerificationStatus.AwaitsVerification);
        } else {
          this.data.push(new TextMessageSenderObservable(data));
        }
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreTextMessageSendersStore] Failed to create text message sender",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      toastModule.triggerToast({
        content: error.message,
      });

      throw error;
    } finally {
      runInAction(() => {
        this.creating = false;
      });
    }
  };

  delete = async (id: string): Promise<void> => {
    runInAction(() => {
      this.deleting = true;
      this.data = this.data.filter(sender => sender.id !== id);
    });

    try {
      const response = await api.delete("/v2/integrations/text-messages/senders/{sender_id}", {
        params: { path: { sender_id: id } },
      });

      const data = response.data;
      if (!data || response.error) {
        const error = response.error as { detail: string };
        throw new Error(error.detail ?? "Failed to delete text message sender");
      }
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreTextMessageSendersStore] Failed to delete text message sender",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      toastModule.triggerToast({
        content: error.message,
      });

      throw error;
    } finally {
      runInAction(() => {
        this.deleting = false;
      });
    }
  };

  verify = async (id: string, verificationCode: string) => {
    runInAction(() => {
      this.verifying = true;
    });

    try {
      const response = await api.post("/v2/integrations/text-messages/senders/{sender_id}/verify", {
        params: { path: { sender_id: id } },
        body: {
          verification_code: verificationCode,
        },
      });

      const data = response.data;
      if (!data || response.error) {
        const error = response.error as { detail: string };
        throw new Error(error.detail ?? "Failed to verify text message sender");
      }

      runInAction(() => {
        const item = this.data.find(item => item.id === id);
        item?.setVerificationStatus(VerificationStatus.Verified);
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreTextMessageSendersStore] Failed to verify text message sender",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      runInAction(() => {
        const item = this.data.find(item => item.id === id);
        if (item) {
          item.setVerificationStatus(VerificationStatus.Invalid);
        } else {
          toastModule.triggerToast({
            content: error.message,
          });
        }
      });
    } finally {
      runInAction(() => {
        this.verifying = false;
      });
    }
  };

  requestNewVerificationCode = async (id: string) => {
    runInAction(() => {
      this.requestingNewVerificationCode = true;
      this.unVerifiedItem?.setVerificationToken("");
    });

    try {
      const response = await api.post(
        "/v2/integrations/text-messages/senders/{sender_id}/request",
        {
          params: { path: { sender_id: id } },
        }
      );

      const data = response.data;
      if (!data || response.error) {
        const error = response.error as { detail: string };
        throw new Error(error.detail ?? "Failed to resend verification code");
      }
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreTextMessageSendersStore] Failed to request new verification code",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      toastModule.triggerToast({
        content: error.message,
      });
    } finally {
      runInAction(() => {
        this.requestingNewVerificationCode = false;
      });
    }
  };

  addUnverifiedItem = () => {
    this.data.push(
      new TextMessageSenderObservable({
        id: AppStoreTextMessageSendersStore.CLIENT_ONLY_ID,
        phone_number: "",
        is_verified: false,
        space_account_id: "",
      })
    );
  };

  clearUnverifiedItem = async () => {
    // if item made it to BE it should have an id
    // if so, we can proceed with deleting it
    if (
      this.unVerifiedItem &&
      this.unVerifiedItem.id !== AppStoreTextMessageSendersStore.CLIENT_ONLY_ID
    ) {
      await this.delete(this.unVerifiedItem.id);
      return;
    }

    // if item is not in BE, we can simply remove it from the list
    this.data = this.data.filter(sender => sender.id !== this.unVerifiedItem?.id);
  };

  loadSettings = async () => {
    if (this.loadingSettings) {
      return;
    }

    runInAction(() => {
      this.loadingSettings = true;
    });

    try {
      const response = await api.get("/v2/integrations/text-messages/settings", {});
      const settings = response.data;

      if (settings) {
        runInAction(() => {
          this.resetSettingFromResponse(settings);
        });
      }
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "Failed to load text message settings",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      toastModule.triggerToast({
        content: "Failed to load text message settings",
      });
    } finally {
      runInAction(() => {
        this.loadingSettings = false;
      });
    }
  };

  saveSettings = async () => {
    runInAction(() => {
      this.savingSettings = true;
    });

    try {
      const response = await api.post("/v2/integrations/text-messages/settings", {
        body: {
          formatting_mode: this._formattingMode as "simple" | "smart",
          formatting_instructions: this._formattingInstructions ?? "",
          organizing_manual_enabled: this._organizingOption.includes("manual"),
          organizing_smart_enabled: this._organizingOption.includes("smart"),
          organizing_instructions: this._organizingInstructions ?? "",
          organizing_manual_collections: this.organizingManualCollections,
        },
      });

      const data = response.data;
      if (!data || response.error) {
        const error = response.error as { detail: string };
        throw new Error(error.detail ?? "Failed to save text message settings");
      }

      runInAction(() => {
        this.resetSettingFromResponse(data);
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreTextMessageSendersStore] Failed to save text message settings",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      toastModule.triggerToast({
        content: error.message,
      });
      throw error;
    } finally {
      runInAction(() => {
        this.savingSettings = false;
      });
    }
  };

  setFormattingOption = async (value: TextMessageIntegrationFormattingMode) => {
    runInAction(() => {
      this._formattingMode = value;
      if (value !== "smart") {
        this._formattingInstructions = null;
      }
    });
    await this.saveSettings();
    trackEvent(TrackedEvent.TextFormattingModeSelect, { mode: value });
  };

  setFormattingInstructions = async (value: TextMessageIntegrationFormattingInstructions) => {
    runInAction(() => {
      this._formattingInstructions = value;
      if (value !== null) {
        this._formattingMode = "smart";
      }
    });
    await this.saveSettings();
    trackEvent(TrackedEvent.TextFormattingSmartModeCustomize, { instructions: value });
  };

  setOrganizingOption = async (value: TextMessageIntegrationOrganizingMode[]) => {
    const prevValue = [...this._organizingOption];

    runInAction(() => {
      this._organizingOption = value;
      if (!value.includes("smart")) {
        this._organizingInstructions = null;
      }

      if (!value.includes("manual")) {
        this._organizingManualCollections = [];
      }
    });
    await this.saveSettings();

    const modes: TextMessageIntegrationOrganizingMode[] = ["smart", "manual"];
    for (const mode of modes) {
      if (prevValue.includes(mode) && !value.includes(mode)) {
        trackEvent(TrackedEvent.TextOrganizingModeUnchecked, { mode });
      }
      if (!prevValue.includes(mode) && value.includes(mode)) {
        trackEvent(TrackedEvent.TextOrganizingModeChecked, { mode });
      }
    }
  };

  setOrganizingInstructions = async (value: TextMessageIntegrationOrganizingInstructions) => {
    runInAction(() => {
      this._organizingInstructions = value;
      if (value !== null) {
        this._organizingOption.push("smart");
      }
    });

    await this.saveSettings();
    trackEvent(TrackedEvent.TextOrganizingSmartModeCustomize, { instructions: value });
  };

  setOrganizingManualCollections = async (
    value: TextMessageIntegrationOrganizingManualCollections
  ) => {
    runInAction(() => {
      this._organizingManualCollections = value;
    });

    await this.saveSettings();

    const collections = await this.store.collections.getManyAsync(value || []);
    trackEvent(TrackedEvent.TextOrganizingSpecificCollectionsSaved, {
      num_collections: collections.length,
      collection_titles: collections.map(collection => collection.title),
    });
  };

  private resetSettingFromResponse = (settings: TextMessageIntegrationSettings) => {
    this._formattingMode = settings.formatting_mode;
    this._formattingInstructions = settings.formatting_instructions || null;

    this._organizingOption = [];
    if (settings.organizing_smart_enabled) this._organizingOption.push("smart");
    if (settings.organizing_manual_enabled) this._organizingOption.push("manual");

    this._organizingInstructions = settings.organizing_instructions || null;
    this._organizingManualCollections = settings.organizing_manual_collections;
  };
}
